import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { presetToRange } from '../components/common/DateRangeFilter';

const DEFAULT_STORAGE_KEY = 'gemini_agents_date_range';

const loadSaved = (defaultPreset, storageKey) => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      const { key, params } = JSON.parse(raw);
      if (key && key !== 'custom') {
        // Recalculate end_time for presets so we always include the latest data
        return { key, params: presetToRange(key) };
      }
      if (key && params) return { key, params };
    }
  } catch { /* ignore corrupt data */ }
  return { key: defaultPreset, params: presetToRange(defaultPreset) };
};

const DateRangeContext = createContext(null);

/**
 * Date-range state for a module's pages.
 *
 * Three modes, in precedence order:
 *
 * **Controlled** — pass `value` + `params` + `onChange` and the provider is a
 * pass-through for a range someone else owns. This is what the Agent Atlas
 * consolidated FinOps view uses: one date filter above the surface switcher
 * drives every embedded module page, so the pills and the numbers underneath
 * cannot disagree. It carries a full `{ start_time, end_time }`, so custom
 * ranges propagate too, and `onChange` makes it two-way — a control inside an
 * embedded page moves the whole view. Deliberately does **not** touch
 * sessionStorage: the embed is borrowing Atlas's window, not redefining the
 * module's own.
 *
 * **Forced preset** — `forcedPreset` adopts an external preset key on mount and
 * whenever it changes, also bypassing sessionStorage. Narrower than controlled
 * (preset keys only, so no custom ranges, and one-way), and kept because it is
 * the lighter option when a caller has nothing but a preset key to hand.
 * Ignored when the controlled props are supplied.
 *
 * **Uncontrolled** (the default) — the provider owns the selection and persists
 * it to sessionStorage, so navigating between a module's pages keeps the same
 * window.
 */
export const DateRangeProvider = ({
  defaultPreset = '30d',
  storageKey = DEFAULT_STORAGE_KEY,
  forcedPreset,
  value,
  params,
  onChange,
  children,
}) => {
  const controlled = Boolean(value && params && onChange);

  // Hooks run unconditionally; the controlled branch simply ignores the state.
  const [dateRange, setDateRange] = useState(() => (
    forcedPreset || loadSaved(defaultPreset, storageKey).key
  ));
  const [dateParams, setDateParams] = useState(() => (
    forcedPreset ? presetToRange(forcedPreset) : loadSaved(defaultPreset, storageKey).params
  ));

  useEffect(() => {
    // Controlled wins: adopting forcedPreset here would fight the owner's value.
    if (!forcedPreset || controlled) return;
    setDateRange(forcedPreset);
    setDateParams(presetToRange(forcedPreset));
  }, [forcedPreset, controlled]);

  const handleDateChange = useCallback((key, range) => {
    setDateRange(key);
    setDateParams(range);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ key, params: range }));
    } catch { /* quota exceeded — ignore */ }
  }, [storageKey]);

  const contextValue = useMemo(() => (controlled
    ? { dateRange: value, dateParams: params, handleDateChange: onChange, controlled: true }
    : { dateRange, dateParams, handleDateChange, controlled: false }
  ), [controlled, value, params, onChange, dateRange, dateParams, handleDateChange]);

  return (
    <DateRangeContext.Provider value={contextValue}>
      {children}
    </DateRangeContext.Provider>
  );
};

export function useDateRange() {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error('useDateRange must be used within a DateRangeProvider');
  }
  return context;
}

export default DateRangeContext;
